/**
 * Utility functions for handling image URLs
 * Supports both local uploads and Cloudinary cloud storage
 */

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || '';

/**
 * Converts a full URL to a relative path for storage
 * This ensures URLs work when preview environments change
 */
export const toRelativeUrl = (url) => {
  if (!url) return '';
  
  // Cloudinary URLs should be stored as-is (they're persistent)
  if (url.includes('cloudinary.com') || url.includes('res.cloudinary.com')) {
    return url;
  }
  
  // If already relative, return as-is
  if (url.startsWith('/api/')) return url;
  
  // Extract the path from full URL
  try {
    const urlObj = new URL(url);
    // Return just the pathname (e.g., /api/files/image/xxx.png)
    return urlObj.pathname;
  } catch {
    // If URL parsing fails, check if it contains /api/files
    const apiIndex = url.indexOf('/api/files');
    if (apiIndex !== -1) {
      return url.slice(apiIndex);
    }
    return url;
  }
};

/**
 * Converts a relative or old URL to a full URL using current base
 * Use this when displaying images
 */
export const toFullUrl = (url) => {
  if (!url) return '';
  
  // Handle Python None/null strings
  if (url === 'None' || url === 'null' || url === 'undefined') return '';
  
  // If it's a Cloudinary URL, return as-is (it's already a full persistent URL)
  if (url.includes('cloudinary.com') || url.includes('res.cloudinary.com')) {
    return url;
  }
  
  // If it's already a full external URL (pexels, unsplash, etc.), return as-is
  if (url.startsWith('http://') || url.startsWith('https://')) {
    // Check if it's an old preview URL that needs updating
    try {
      const urlObj = new URL(url);
      if (urlObj.hostname.includes('preview.emergentagent.com') || 
          urlObj.hostname.includes('preview.emergent')) {
        // Extract just the path and rebuild with current base
        return API_BASE_URL + urlObj.pathname;
      }
    } catch {
      // URL parsing failed, return as-is
    }
    return url;
  }
  
  // If it's a relative URL starting with /api/, prepend current base
  if (url.startsWith('/api/')) {
    return API_BASE_URL + url;
  }
  
  // Handle old /uploads/ paths - these don't exist anymore, return empty to show fallback
  if (url.startsWith('/uploads/') || url.includes('/uploads/')) {
    console.warn('Old upload URL format detected, using fallback:', url);
    return ''; // Return empty to trigger fallback image
  }
  
  // Unknown format - return empty to show fallback
  return '';
};

/**
 * Check if a URL is valid and accessible
 */
export const isValidImageUrl = (url) => {
  if (!url) return false;
  if (url === 'None' || url === 'null' || url === 'undefined') return false;
  if (typeof url !== 'string') return false;
  if (url.trim() === '') return false;
  
  // Block known problematic domains that have hotlinking protection
  const blockedDomains = [
    'wsj.com',
    'nytimes.com',
    'ft.com',
    'bloomberg.com'
  ];
  
  if (blockedDomains.some(domain => url.includes(domain))) {
    return false;
  }
  
  // Accept any valid HTTP/HTTPS URL or relative API URL
  return url.startsWith('http://') || 
         url.startsWith('https://') || 
         url.startsWith('/api/');
};

/**
 * Check if URL is a Cloudinary URL
 */
export const isCloudinaryUrl = (url) => {
  if (!url || typeof url !== 'string') return false;
  return url.includes('cloudinary.com') || url.includes('res.cloudinary.com');
};

export default {
  toRelativeUrl,
  toFullUrl,
  isValidImageUrl,
  isCloudinaryUrl
};
